import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import { prProfileUrl } from "views_shared/app/javascript/utils/urls";
import ProfilePage from "../profiles/ProfilePage";
import useScrollToHashSection from "../profiles/use_scroll_to_hash_section";
import ViewersAlsoView, { useViewersAlsoView } from "../profiles/ViewersAlsoView";
import ChecklistCard from "../profiles/ChecklistCard/ChecklistCard";
import useProfileCompletion from "../profiles/ChecklistCard/use_profile_completion";
import ConnectSection, { connectTitle, firstName } from "../profiles/sections/ConnectSection";
import { PrProfileContext } from "./data/pr_profile_context_management";
import useTeamMembers from "./data/use_team_members";
import PrPersonHeader from "./show/pr_person_header";
import PrSections from "./show/pr_sections";
import PrProfileSkeleton from "./show/pr_profile_skeleton";
import TeamMembersCard from "./show/team_members_card";
import PrProfileEditModal from "./edit/edit_modal";

// The Checklist card's factor list for a PR profile, in the design's row order (Add a headshot / bio
// / employment / header information / contact info / products+experts represented) — the six
// boolean columns id_pr_profile_completion_scores_view exposes, per PR's 6-factor completion set
// ("header information" has no dedicated column; it's the same "fill in your identity" idea as the
// headline, so it maps onto has_headline rather than inventing a 7th factor).
const PR_CHECKLIST_FACTORS = [
  { key: "has_profile_image", label: "Add a headshot" },
  { key: "has_description", label: "Add a bio" },
  { key: "has_jobs", label: "Add your employment" },
  { key: "has_headline", label: "Add header information" },
  { key: "has_contact_details", label: "Add contact info" },
  { key: "has_representatives", label: "Add products or experts you represent" },
];

// Hydrate adapter for the Viewers-also-view rail: loads display rows for the similar-profile ids
// through the pr_profiles view, whose RLS drops any profile this viewer can't see.
const hydrateSimilarPrProfiles = async (ids) => {
  const response = await qapi.prProfiles.getAllById(ids);
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows.map((row) => ({
    id: row.id,
    href: prProfileUrl({ slug: row.slug, migrate_id: row.migrate_id }),
    imageUrl: row.profile_image_url,
    title: row.name,
    subtitle: row.title_at_company,
  }));
};

// PR Profile show (the inner component under PrProfileTopLevelShow's provider): renders the shared
// <ProfilePage> chassis with a <PersonHeader> + a stack of section cards, plus a right rail of the
// Team Members card and the shared Viewers-also-view similarity card. Mirrors CompanyShow.
function PrProfile({ prProfileId }) {
  const { setPrProfileId, isLoading, asyncError, prProfile, employments, viewerContext, setOpenEditModal } =
    useContext(PrProfileContext);
  const editable = !!viewerContext?.editable;

  // Deep-links checklist rows to the edit modal section that actually fixes them. `has_representatives`
  // stays static: representing is split across three separate modals (EXPERTS/PRODUCTS/COMPANIES), so
  // there is no single routed key to point that row at.
  const checklistFactorActions = {
    has_description: () => setOpenEditModal("HEADER"),
    has_headline: () => setOpenEditModal("HEADER"),
    has_jobs: () => setOpenEditModal("EMPLOYMENT"),
  };

  // Owner/admin-only widget: idling the hook (rather than just gating the render) for a non-editable
  // viewer means it never even issues the read. A collapsed card still renders its header, so it
  // still counts as rail content; a hidden one doesn't.
  const checklist = useProfileCompletion({
    resourceClient: qapi.prProfileCompletionScores,
    id: editable ? prProfile?.id : null,
    kind: "pr_profile",
    refreshKey: prProfile,
  });
  const checklistContributes = editable && !checklist.hidden && (checklist.isLoading || !!checklist.score);

  // The similarity query needs the numeric profile id (the route param may be a slug), so it idles
  // until the profile row has loaded — which the page-level skeleton covers anyway.
  const viewersAlsoView = useViewersAlsoView({
    queryModelType: "Id::Profile::Promoter",
    queryModelId: prProfile?.id,
    hydrate: hydrateSimilarPrProfiles,
  });

  // Team Members hangs off ONE employment. `employments` arrives featured-first (the provider's
  // invariant), so the first qualifying row is the job the header already presents as where they work.
  // An ended job's colleagues aren't their team any more, and a job hidden from their own profile must
  // not resurrect its company here. No qualifying job -> no company -> the hook settles empty.
  const teamEmployment = (employments || []).find((job) => !job.to && !job.personHidden);
  // The profile and employment reads run concurrently, so employment can resolve first. The hook's
  // guard alone only requires a companyId, but here `excludePromoterId` IS the viewed profile's own id —
  // withholding companyId until that id is known keeps this caller's very first roster request already
  // self-excluding, instead of firing one unfiltered request and then a filtered refetch.
  const teamMembers = useTeamMembers({
    companyId: prProfile?.id ? teamEmployment?.companyId : null,
    excludePromoterId: prProfile?.id,
  });

  // The read-only Connect card holds only contact details (Message lives in the header), so with
  // none it collapses rather than showing an empty card.
  const contactDetails = prProfile?.contact_details || [];
  const connectHasContent = contactDetails.length > 0;

  useEffect(() => {
    setPrProfileId(prProfileId);
  }, [prProfileId, setPrProfileId]);

  // Deep-linked scroll for a PR card's "Represents" link (`#pr-section-represents`).
  useScrollToHashSection(!isLoading);

  // Let the top-level ErrorBoundary handle async failures.
  if (asyncError) throw asyncError;

  // Rails found the profile but the API returned no row — the viewer isn't entitled to it (the
  // PostgREST view enforces visibility/RLS; the Rails route doesn't). Say so instead of
  // skeleton-ing forever.
  if (!isLoading && !prProfile) {
    return <div className="alert alert-secondary">This profile isn&apos;t available.</div>;
  }

  // ProfilePage sizes its columns on `rightRail` truthiness alone, so a fragment of self-collapsing
  // cards would leave a phantom rail: the page has to know every card's state. The rail stays while
  // any card has items or is still loading, and goes away only once all of them settle empty — then
  // the main column takes the full width. The one-time reflow that causes is confined to that case;
  // the common case renders rail skeletons, then items, with no layout snap.
  const railHasContent =
    checklistContributes ||
    connectHasContent ||
    teamMembers.isLoading ||
    teamMembers.members.length > 0 ||
    viewersAlsoView.isLoading ||
    viewersAlsoView.items.length > 0;

  return (
    <div>
      <PrProfileEditModal />

      <ProfilePage
        isLoading={isLoading}
        skeleton={<PrProfileSkeleton />}
        header={<PrPersonHeader />}
        rightRail={
          railHasContent ? (
            <>
              {editable && !checklist.hidden && (
                <ChecklistCard
                  score={checklist.score}
                  factors={PR_CHECKLIST_FACTORS}
                  isLoading={checklist.isLoading}
                  factorActions={checklistFactorActions}
                  onHide={checklist.hide}
                />
              )}
              {connectHasContent && (
                <ConnectSection
                  id="pr-rail-connect"
                  title={connectTitle(prProfile?.first_name || firstName(prProfile?.name))}
                  contactDetails={contactDetails}
                />
              )}
              <TeamMembersCard
                members={teamMembers.members}
                totalCount={teamMembers.totalCount}
                isLoading={teamMembers.isLoading}
              />
              <ViewersAlsoView items={viewersAlsoView.items} isLoading={viewersAlsoView.isLoading} />
            </>
          ) : null
        }
      >
        <PrSections />
      </ProfilePage>
    </div>
  );
}

PrProfile.propTypes = {
  prProfileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default PrProfile;
